import JsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const CLARET = [139, 23, 59];

const pct = (v) => `${Math.round((v || 0) * 1000) / 10}%`;
const n = (v) => (v ?? 0).toLocaleString();

// A one-page partner report: what happened after graduates of the chosen
// cohorts were sent job recommendations.
export default function buildJobsPdf(summary, { cohortNames, campaignName }) {
  const doc = new JsPDF({ unit: 'mm', format: 'a4' });
  const t = summary.total;
  const left = 14;

  doc.setFillColor(...CLARET);
  doc.rect(0, 0, 210, 26, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.text('Graduate job outcomes', left, 13);
  doc.setFontSize(9);
  doc.text('Power Learn Project', left, 20);

  doc.setTextColor(31, 41, 51);
  doc.setFontSize(9);
  const scope = doc.splitTextToSize(
    `Cohorts: ${cohortNames.join(', ') || 'All'}${
      campaignName ? `   ·   Campaign: ${campaignName}` : ''
    }`,
    182
  );
  doc.text(scope, left, 34);
  let y = 34 + scope.length * 4 + 2;
  doc.setTextColor(100, 110, 120);
  doc.text(
    `Generated ${new Date(summary.generated_at).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })}`,
    left,
    y
  );
  y += 6;

  autoTable(doc, {
    startY: y,
    theme: 'grid',
    head: [['', 'Graduates', 'Share of those emailed']],
    body: [
      ['Certified graduates in these cohorts', n(t.certified_graduates), ''],
      ['Emailed tailored job openings', n(t.graduates_emailed), '100%'],
      ['Opened at least one job', n(t.graduates_clicked), pct(t.click_rate)],
      [
        'Applied (proof checked by PLP)',
        n(t.graduates_applied),
        pct(t.apply_rate),
      ],
      ['Reported getting a job', n(t.graduates_hired), pct(t.hire_rate)],
      ['…of which, a job from our emails', n(t.graduates_hired_via_digest), ''],
    ],
    headStyles: { fillColor: CLARET },
    styles: { fontSize: 9 },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' } },
  });
  y = doc.lastAutoTable.finalY + 8;

  if (summary.cohorts?.length > 1) {
    doc.setFontSize(11);
    doc.setTextColor(31, 41, 51);
    doc.text('By cohort', left, y);
    autoTable(doc, {
      startY: y + 3,
      theme: 'striped',
      head: [['Cohort', 'Emailed', 'Opened', 'Applied', 'Hired']],
      body: summary.cohorts.map((c) => [
        c.cohort || c.cohort_id,
        n(c.graduates_emailed),
        pct(c.click_rate),
        pct(c.apply_rate),
        pct(c.hire_rate),
      ]),
      headStyles: { fillColor: CLARET },
      styles: { fontSize: 8 },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  if (summary.top_applied_jobs?.length) {
    doc.setFontSize(11);
    doc.text('Roles graduates applied for most', left, y);
    autoTable(doc, {
      startY: y + 3,
      theme: 'striped',
      head: [['Role', 'Employer', 'Applications']],
      body: summary.top_applied_jobs.map((j) => [
        j.title,
        j.company || '',
        n(j.applications),
      ]),
      headStyles: { fillColor: CLARET },
      styles: { fontSize: 8 },
      columnStyles: { 2: { halign: 'right' } },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  doc.setFontSize(7.5);
  doc.setTextColor(100, 110, 120);
  doc.text(
    doc.splitTextToSize(
      'How these figures are counted: graduates are emailed early-career tech jobs matched to their training. ' +
        '"Opened" means they clicked through to a listing. "Applied" counts only graduates who uploaded proof of an ' +
        'application that PLP staff checked and approved. "Got a job" is self-reported. Test emails and dry runs are excluded.',
      182
    ),
    left,
    Math.min(y, 270)
  );
  return doc;
}
